import {
  NotificationsOffOutlined,
  NotificationsOutlined,
  WarningAmberOutlined,
} from '@mui/icons-material';
import { IconButton, Tooltip } from '@mui/material';
import { useEffect, useState } from 'react';
import { updateProAlertMuted } from '../../../api/pro-alert';
import { ProAlert, ProAlertDialogStatus } from '../../../models/pro.model';

interface MyFleetVesselAlertToggleProps {
  proAlert?: ProAlert | null;
  vesselImo?: string | null;
}

function MyFleetVesselAlertToggle({
  proAlert,
  vesselImo,
}: MyFleetVesselAlertToggleProps) {
  const [status, setStatus] = useState(ProAlertDialogStatus.Idle);
  const [alertMuted, setAlertMuted] = useState(proAlert?.muted ?? false);

  const handleToggleAlertActive = async () => {
    if (!proAlert || !proAlert.alert_id) return;
    try {
      setStatus(ProAlertDialogStatus.Loading);
      const response = await updateProAlertMuted({
        alert_id: proAlert?.alert_id,
        muted: !alertMuted,
      });
      if (response.muted !== undefined) {
        setAlertMuted(response.muted);
      }
      setStatus(ProAlertDialogStatus.Idle);
    } catch (error) {
      setStatus(ProAlertDialogStatus.Error);
    }
  };

  const renderAlertIcon = () => {
    if (status === ProAlertDialogStatus.Error) {
      return (
        <Tooltip
          title="Failed to update alert. Please try again later. If this continues please contact support."
          arrow
        >
          <WarningAmberOutlined color="warning" fontSize="small" />
        </Tooltip>
      );
    }
    if (alertMuted) {
      return <NotificationsOffOutlined fontSize="small" />;
    }
    return <NotificationsOutlined fontSize="small" />;
  };

  useEffect(() => {
    let timeout: NodeJS.Timeout;
    if (status === ProAlertDialogStatus.Error) {
      timeout = setTimeout(() => setStatus(ProAlertDialogStatus.Idle), 5000);
    }
    return () => clearTimeout(timeout);
  }, [status]);

  return (
    <Tooltip title={alertMuted ? 'PRO Alert Disabled' : 'PRO Alert Enabled'}>
      <span>
        <IconButton
          sx={{ fontSize: '22px', color: 'secondary.main' }}
          onClick={() => handleToggleAlertActive()}
          aria-label="My fleet vessel alert toggle"
          data-testid={`vessel-alert-toggle-button-${vesselImo}`}
          disabled={status === ProAlertDialogStatus.Loading}
        >
          {renderAlertIcon()}
        </IconButton>
      </span>
    </Tooltip>
  );
}

export default MyFleetVesselAlertToggle;

MyFleetVesselAlertToggle.defaultProps = {
  proAlert: null,
  vesselImo: null,
};
